import { useInjection } from 'inversify-react';
import { forwardRef, useCallback, useEffect, useRef } from 'react';
import root from 'react-shadow/styled-components';
import { useIntersection } from 'react-use';

import { VideoManager } from '../../../../core/viewer/video-manager';
import { TYPES } from '../../../../inversify.types';
import { IViewerStoreInjector } from '../../../../store';
import { StyledSvgContainer } from '../Viewer.styles';
import { useVideoEventListeners } from '../hooks/useVideoEventListeners';
import { StyledShadowDOMWrapper } from './ShadowRootContent.styles';

type ShadowRootContentProps = {
  content: string;
  pageId: number;
};

export const ShadowRootContent = forwardRef<HTMLDivElement, ShadowRootContentProps>(
  ({ content, pageId }, shadowDomRef) => {
    const intersectionObserverRef = useRef<HTMLDivElement | null>(null);

    const videoManager = useInjection<VideoManager>(TYPES.Viewer.VideoManager);
    const useViewerStore = useInjection<IViewerStoreInjector>(TYPES.Store.Viewer);
    const zoomLevel = useViewerStore((state) => state.zoom / 100);

    const { callbackRef: videoEventListenersRef } = useVideoEventListeners({
      pageId,
    });

    const setupVideoSiblings = useCallback(
      (node: HTMLDivElement | null) => {
        if (!node) return;

        // Create a content hash to detect changes
        const contentHash = `${pageId}-${content.length}`;

        // Let VideoManager handle the setup with duplication check
        videoManager.setupVideoSiblingsIfNeeded(node, contentHash);
      },
      [pageId, content, videoManager]
    );

    const intersection = useIntersection(intersectionObserverRef, {
      threshold: 0.05,
    });

    useEffect(() => {
      if (!intersection?.isIntersecting) {
        videoManager.pauseOutOfViewVideo({
          pageId,
          rootElement: intersectionObserverRef.current,
        });
      }
    }, [intersection?.isIntersecting, videoManager, pageId]);

    const shadowDomWrapperRef = useCallback(
      (node: HTMLDivElement | null) => {
        // Always call the video event listeners callback ref
        videoEventListenersRef(node);

        // Handle Safari-specific video sibling setup
        if (videoManager.isSafari) {
          setupVideoSiblings(node);
        }
      },
      [videoEventListenersRef, videoManager.isSafari, setupVideoSiblings]
    );

    return (
      <root.div allowFullScreen ref={intersectionObserverRef} data-shadow-root-container>
        {/* Apply CSS zoom to scale the entire SVG container and its sibling video elements.
          In Safari, videos are embedded as DOM siblings to the SVG (not using foreignObject),
          so applying zoom here ensures both SVG and video elements scale together uniformly,
          maintaining correct positioning at all zoom levels. */}
        <StyledShadowDOMWrapper
          style={{
            zoom: zoomLevel,
          }}
          ref={shadowDomWrapperRef}
          data-svg-container
        >
          <div ref={shadowDomRef}>
            <StyledSvgContainer
              dangerouslySetInnerHTML={{
                __html: content,
              }}
            />
          </div>
        </StyledShadowDOMWrapper>
      </root.div>
    );
  }
);

ShadowRootContent.displayName = 'ShadowRootContent';
